// Destination editorial content — the per-(location, locale) JSONB payload
// stored in app.destination_content (0141) and rendered on the
// /car-rental/{country}/{place} destination pages. Shape mirrors the
// hand-authored SeoLandingContent (apps/web/src/lib/seo/landing/registry.ts)
// minus links (internal linking is computed, not authored) and h1 (the ICU
// template stays canonical). Zod-validated at every boundary: the generator
// validates model output against it, the API trusts only parsed payloads,
// and the web render receives the inferred type.
//
// Editorial discipline (enforced by the generator's code validators, not
// just prompts): NO prices, ratings, review counts, or car counts in prose —
// live numbers only ever enter at render via interpolation; no years or
// "currently"-style time anchors (content must be evergreen); country-level
// driving RULES live in app.country_driving_facts, never here (drivingTips
// is destination-level texture: parking, traffic character, local routes).
import { z } from 'zod';

export const DESTINATION_CONTENT_LOCALES = ['en', 'fr', 'ar', 'es'] as const;
export type DestinationContentLocale = (typeof DESTINATION_CONTENT_LOCALES)[number];

export const SEASONS = ['spring', 'summer', 'autumn', 'winter'] as const;
export type Season = (typeof SEASONS)[number];

export const DestinationContentSchema = z.object({
  /** <= 70 chars; unique per destination (overrides the ICU title template). */
  metaTitle: z.string().min(10).max(70),
  /** <= 170 chars; overrides the ICU description template. */
  metaDescription: z.string().min(50).max(170),
  /** Short positioning line under the H1. */
  tagline: z.string().min(1).max(160),
  /** 2-3 sentence server-rendered intro. */
  lede: z.string().min(1),
  /** 35-70 word direct answer — the AI-liftable block (AnswerBox). The word
   *  bounds are enforced by the generator's validators (word counts are not
   *  expressible in JSON Schema / zod string constraints). */
  answer: z.string().min(120),
  sections: z
    .array(
      z.object({
        heading: z.string().min(1).max(120),
        body: z.array(z.string().min(1)).min(1).max(6),
      }),
    )
    .min(3)
    .max(6),
  /** Season-by-season guidance, evergreen framing. 2-4 entries (equatorial
   *  destinations legitimately have fewer distinct seasons). */
  seasonal: z
    .array(
      z.object({
        season: z.enum(SEASONS),
        heading: z.string().min(1).max(120),
        body: z.string().min(1),
      }),
    )
    .min(2)
    .max(4),
  /** Destination-LEVEL driving texture only (parking, traffic, local
   *  routes). Country RULES render from country_driving_facts. */
  drivingTips: z
    .array(z.object({ heading: z.string().min(1).max(120), body: z.string().min(1) }))
    .max(6)
    .default([]),
  bestFor: z
    .array(z.object({ title: z.string().min(1).max(80), body: z.string().min(1) }))
    .max(6)
    .default([]),
  /** Unique authored FAQs. Topic partitioning vs the programmatic FAQs
   *  (price/count/pickup) and facts FAQs (IDP/drive-side) is enforced by
   *  the generator prompt + validators. */
  faqs: z
    .array(z.object({ question: z.string().min(1).max(200), answer: z.string().min(1) }))
    .min(5)
    .max(8),
});

export type DestinationContent = z.infer<typeof DestinationContentSchema>;

/** Flattened prose of a payload — the single definition of "how much text
 *  does this content amount to", used for plain_length (which feeds the
 *  indexability engine's per-language content thresholds) and for the
 *  generator's uniqueness shingling. Headings included: they are rendered
 *  crawlable text. */
export function destinationContentPlainText(c: DestinationContent): string {
  return [
    c.tagline,
    c.lede,
    c.answer,
    ...c.sections.flatMap((s) => [s.heading, ...s.body]),
    ...c.seasonal.flatMap((s) => [s.heading, s.body]),
    ...c.drivingTips.flatMap((t) => [t.heading, t.body]),
    ...c.bestFor.flatMap((b) => [b.title, b.body]),
    ...c.faqs.flatMap((f) => [f.question, f.answer]),
  ]
    .join('\n')
    .trim();
}

// ─── Country driving facts (app.country_driving_facts, 0141) ─────────────
// Human-verified rule facts, keyed by ISO-2. The generator may RESTATE these
// (consistency-checked) but the render layer prints them from this row
// directly, so a wrong restatement can never outrank the canonical box.

export const CountryDrivingFactsSchema = z.object({
  countryCode: z.string().length(2),
  driveSide: z.enum(['left', 'right']),
  idpStatus: z.enum(['required', 'recommended', 'not_needed']),
  idpNote: z.string().nullable(),
  minAge: z.number().int().min(15).max(30).nullable(),
  youngDriverNote: z.string().nullable(),
  speedUnits: z.enum(['kmh', 'mph']),
  speedUrban: z.number().int().nullable(),
  speedRural: z.number().int().nullable(),
  speedMotorway: z.number().int().nullable(),
  tollsSummary: z.string().nullable(),
  bacLimit: z.string().nullable(),
  emergencyNumber: z.string().nullable(),
  keyRules: z.array(z.string()).default([]),
  /** Per-locale overrides for the free-text fields (idpNote,
   *  youngDriverNote, tollsSummary, keyRules). Enum/numeric fields render
   *  via i18n labels and need no translation here. */
  locales: z
    .record(
      z.object({
        idpNote: z.string().nullable().optional(),
        youngDriverNote: z.string().nullable().optional(),
        tollsSummary: z.string().nullable().optional(),
        keyRules: z.array(z.string()).optional(),
      }),
    )
    .default({}),
});

export type CountryDrivingFacts = z.infer<typeof CountryDrivingFactsSchema>;

/** The facts row with locale overrides applied for one language (en = base). */
export function localizedDrivingFacts(
  facts: CountryDrivingFacts,
  locale: string,
): CountryDrivingFacts {
  if (locale === 'en') return facts;
  const o = facts.locales[locale];
  if (!o) return facts;
  return {
    ...facts,
    idpNote: o.idpNote !== undefined ? o.idpNote : facts.idpNote,
    youngDriverNote: o.youngDriverNote !== undefined ? o.youngDriverNote : facts.youngDriverNote,
    tollsSummary: o.tollsSummary !== undefined ? o.tollsSummary : facts.tollsSummary,
    keyRules: o.keyRules !== undefined ? o.keyRules : facts.keyRules,
  };
}
